import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Input, Modal, Space, Table, Tag, Tooltip, message } from 'antd';
import { PlusOutlined, SearchOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Edit, Trash2, ShieldCheck, Copy, Eye } from 'lucide-react';
import Layout from '../../../Layout';
import useTableColumnSettings, { SettingColumn } from '../../../hooks/useTableColumnSettings';
import { ColumnSettingButton } from '../../../components/TableColumnSetting';
import PermissionGroupModal from './PermissionGroupModal';
import AccessScopeDrawer from './AccessScopeDrawer';
import type { RoleGroup, RoleGroupFormValues } from './types';
import { getTotalPermissionCount, splitPermissions } from '../../../config/permissionModules';
import {
    createRole,
    deleteRole,
    getRoles,
    updateRole,
    type RoleItem,
    type RolePayload,
} from '../../../api/Role';
import useAuthStore from "../../../store/authStore";

function mapRoleItemToRoleGroup(item: RoleItem): RoleGroup {
    return {
        id: item.id,
        name: item.name,
        description: item.description ?? '',
        permissions: Array.isArray(item.permissions) ? item.permissions : [],
        user_count: item.user_count ?? item.users_count ?? 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
    };
}

function buildRolePayload(values: RoleGroupFormValues): RolePayload {
    return {
        name: values.name,
        description: values.description ?? '',
        permissions: values.permissions ?? [],
    };
}

export default function GroupRolesPage() {
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("group_roles.create");
    const canEdit = isSuperAdmin || hasPermission("group_roles.edit");
    const canDelete = isSuperAdmin || hasPermission("group_roles.delete");
    const [data, setData] = useState<RoleGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [keyword, setKeyword] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<'add' | 'edit'>('add');
    const [editing, setEditing] = useState<RoleGroup | null>(null);
    // Dữ liệu điền sẵn khi tạo nhóm mới bằng cách sao chép nhóm có sẵn
    const [copySource, setCopySource] = useState<RoleGroupFormValues | null>(null);
    // Nhóm đang xem phạm vi truy cập (các màn hình vào được)
    const [viewing, setViewing] = useState<RoleGroup | null>(null);

    const totalPerm = getTotalPermissionCount();

    const fetchList = async () => {
        setLoading(true);
        try {
            const res = await getRoles({ take: 100 });
            if (!res.status) {
                message.error('Không tải được danh sách nhóm quyền');
                setData([]);
                return;
            }
            const raw = res.data;
            const items: RoleItem[] = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.items)
                ? raw!.items!
                : [];
            setData(items.map(mapRoleItemToRoleGroup));
        } catch (err) {
            console.error(err);
            message.error('Không tải được danh sách nhóm quyền');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const filtered = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        if (!kw) return data;
        return data.filter(
            (g) =>
                g.name.toLowerCase().includes(kw) ||
                (g.description ?? '').toLowerCase().includes(kw)
        );
    }, [data, keyword]);

    const openAdd = () => {
        setMode('add');
        setEditing(null);
        setCopySource(null);
        setModalOpen(true);
    };

    const openCopy = (record: RoleGroup) => {
        setMode('add');
        setEditing(null);
        setCopySource({
            name: `${record.name} (bản sao)`,
            description: record.description ?? '',
            permissions: splitPermissions(record.permissions).valid,
        });
        setModalOpen(true);
    };

    const openEdit = (record: RoleGroup) => {
        setMode('edit');
        setEditing(record);
        setCopySource(null);
        setModalOpen(true);
    };

    const handleSubmit = async (values: RoleGroupFormValues) => {
        setSubmitting(true);
        try {
            const payload = buildRolePayload(values);
            if (mode === 'add') {
                const res = await createRole(payload);
                if (!res.status) {
                    message.error(res.msg || 'Tạo nhóm quyền thất bại');
                    return;
                }
                message.success('Tạo nhóm quyền thành công');
            } else if (editing) {
                const res = await updateRole(editing.id, payload);
                if (!res.status) {
                    message.error(res.msg || 'Cập nhật nhóm quyền thất bại');
                    return;
                }
                message.success('Cập nhật nhóm quyền thành công');
            }
            setModalOpen(false);
            fetchList();
        } catch {
            message.error('Có lỗi xảy ra');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (record: RoleGroup) => {
        Modal.confirm({
            title: 'Xóa nhóm quyền?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    Bạn có chắc muốn xóa nhóm quyền{' '}
                    <span className="font-semibold">{record.name}</span>?
                    {(record.user_count ?? 0) > 0 && (
                        <div className="mt-2 text-red-500 text-sm">
                            Đang có {record.user_count} nhân viên thuộc nhóm này. Họ sẽ mất quyền sau khi xóa.
                        </div>
                    )}
                </div>
            ),
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            centered: true,
            onOk: async () => {
                const res = await deleteRole(record.id);
                if (!res.status) {
                    message.error(res.msg || 'Xóa nhóm quyền thất bại');
                    return;
                }
                message.success('Đã xóa nhóm quyền');
                fetchList();
            },
        });
    };

    const columns: SettingColumn<RoleGroup>[] = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 70,
        },
        {
            title: 'Tên nhóm quyền',
            dataIndex: 'name',
            render: (text: string, record) => (
                <div className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 mt-1 text-[#3e91ab]" />
                    <div>
                        <div className="font-medium text-gray-900">{text}</div>
                        {record.description && (
                            <div className="text-xs text-gray-500 mt-0.5 max-w-[360px]">
                                {record.description}
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Số quyền',
            dataIndex: 'permissions',
            width: 130,
            render: (perms: string[]) => {
                // Chỉ đếm quyền còn trong danh mục; quyền cũ được dọn khi lưu nhóm
                const { valid, stale } = splitPermissions(perms ?? []);
                const count = valid.length;
                const full = count === totalPerm;
                return (
                    <Tooltip
                        title={`${count} / ${totalPerm} quyền${stale.length ? ` · ${stale.length} quyền cũ không còn dùng` : ''}`}
                    >
                        <Tag color={full ? 'gold' : count > 0 ? 'cyan' : 'default'}>
                            {full ? 'Toàn quyền' : `${count}/${totalPerm}`}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Cập nhật',
            dataIndex: 'updated_at',
            width: 160,
            render: (v?: string) =>
                v ? new Date(v).toLocaleString('vi-VN') : '—',
        },
        {
            key: 'actions',
            title: 'Thao tác',
            width: 170,
            fixed: 'right',
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setViewing(record)}
                        className="!text-gray-600 bg-gray-50 hover:bg-gray-100 p-1.5 rounded-md transition-colors"
                        title="Xem phạm vi truy cập"
                    >
                        <Eye className="h-4 w-4" />
                    </button>
                    {canCreate && (
                        <button
                            onClick={() => openCopy(record)}
                            className="!text-[#3e91ab] bg-cyan-50 hover:bg-cyan-100 p-1.5 rounded-md transition-colors"
                            title="Sao chép thành nhóm mới"
                        >
                            <Copy className="h-4 w-4" />
                        </button>
                    )}
                    {canEdit && (
                        <button
                            onClick={() => openEdit(record)}
                            className="!text-[#3e91ab] bg-yellow-50 hover:bg-yellow-100 p-1.5 rounded-md transition-colors"
                            title="Sửa"
                        >
                            <Edit className="h-4 w-4" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={() => handleDelete(record)}
                            className="!text-red-500 bg-red-50 hover:bg-red-100 p-1.5 rounded-md transition-colors"
                            title="Xóa"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings('role_group_list', columns, { scrollX: 950 });

    return (
        <Layout>
            <Helmet>
                <title>Nhóm quyền | Khothi</title>
            </Helmet>

            <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Nhóm quyền</h2>
                        <p className="text-sm text-gray-500">
                            Tạo nhóm quyền tùy chỉnh bằng cách chọn các quyền từ những module có trong hệ thống.
                        </p>
                    </div>

                    <Space wrap>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Tìm theo tên, mô tả..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            className="w-[260px]"
                        />
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                            >
                                Thêm nhóm quyền
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    rowKey="id"
                    loading={loading}
                    dataSource={filtered}
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    scroll={{ x: scrollX }}
                />
            </div>

            <PermissionGroupModal
                open={modalOpen}
                mode={mode}
                initialValue={mode === 'edit' ? editing : copySource}
                loading={submitting}
                onCancel={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />

            <AccessScopeDrawer role={viewing} onClose={() => setViewing(null)} />
        </Layout>
    );
}
